import { useDisclosure } from "@mantine/hooks"
import {
  getFilteredRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table"
import { useMemo, useState } from "react"

import { useCustomReactTable } from "~/components/hooks/useCustomReactTable"
import { CustomTable } from "~/components/table/CustomTable"
import {
  FindIngestionsResponseDto,
  FindIngestionsResponseDtoStatusEnumKey,
  useImportBlankProducts,
  useImportProducts,
} from "~/gen/api"
import { ErrorPreviewModal } from "~/pages/Admin/Ingestion/ProductImport/components/ErrorPreviewModal"
import { ProductImportModal } from "~/pages/Admin/Ingestion/ProductImport/components/ProductImportModal"
import { useIngestionListColumns } from "~/pages/Admin/Ingestion/ProductImport/ingestionsLists.columns"

export type TraceabilityLaunchList = {
  id: string
  owner: string
  product_count: number
  traceabilityLaunchedAt?: Date | null
  status: FindIngestionsResponseDtoStatusEnumKey
  document: {
    id: string
    name: string
  }
}

type Props = {
  ingestions: FindIngestionsResponseDto[]
}

export function IngestionList({ ingestions }: Props) {
  const [errorModalOpened, errorModalActions] = useDisclosure(false)
  const [importModalOpened, importModalActions] = useDisclosure(false)
  const [selectedIngestionId, setSelectedIngestionId] = useState<string>("")

  const [selectedIngestion, setSelectedIngestion] =
    useState<FindIngestionsResponseDto | null>(null)

  const importProductsMutationOptions = useMemo(
    () => ({
      mutation: {
        onSuccess() {
          importModalActions.close()
        },
      },
    }),
    [importModalActions]
  )

  const { mutate: importProducts, isPending: isImportingProducts } =
    useImportProducts(importProductsMutationOptions)

  const { mutate: importBlankProducts, isPending: isImportingBlankProducts } =
    useImportBlankProducts(importProductsMutationOptions)

  const [isSelectedIngestionBlank, setIsSelectedIngestionBlank] =
    useState<boolean>(false)

  const handleErrorModalOpen = (ingestionId: string) => {
    setSelectedIngestionId(ingestionId)
    errorModalActions.open()
  }

  const handleImportModalOpen = (
    ingestion: FindIngestionsResponseDto,
    isBlankIngestion: boolean
  ) => {
    setIsSelectedIngestionBlank(isBlankIngestion)
    setSelectedIngestion(ingestion)
    importModalActions.open()
  }

  const columns = useIngestionListColumns({
    handleErrorModalOpen,
    handleImportModalOpen,
  })

  const table = useCustomReactTable({
    data: ingestions,
    columns,

    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      sorting: [
        {
          id: "createdAt",
          desc: true,
        },
      ],
    },
  })

  return (
    <>
      <CustomTable table={table} pagination={true} />
      <ErrorPreviewModal
        ingestionId={selectedIngestionId}
        opened={errorModalOpened}
        onClose={errorModalActions.close}
      />
      <ProductImportModal
        isBlankIngestion={isSelectedIngestionBlank}
        ingestionId={selectedIngestion?.id ?? ""}
        isImportingProducts={isImportingProducts}
        isImportingBlankProducts={isImportingBlankProducts}
        productsImport={importProducts}
        importBlankProducts={importBlankProducts}
        opened={importModalOpened}
        close={importModalActions.close}
        productsVerifiedCount={selectedIngestion?.product_count ?? 0}
        userEmail={selectedIngestion?.createdBy?.email ?? ""}
      />
    </>
  )
}
